import React from 'react';
import { Modal, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFetchSubscriptionStatusQuery } from '../../../store/apis/subscriptionApi';

/**
 * Modal component for handling insufficient credits error
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether modal is visible
 * @param {Function} props.onHide - Handler to close modal
 * @param {string} props.feature - Feature name that requires credits (e.g., 'classification')
 * @param {number} props.creditsNeeded - Number of credits needed for the operation
 */
const CreditsRequiredModal = ({
  show,
  onHide,
  feature = 'classification',
  creditsNeeded = 1,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch current subscription status to get credits info
  const { data: subscriptionData } = useFetchSubscriptionStatusQuery(undefined, {
    skip: !show, // Only fetch when modal is open
    refetchOnMountOrArgChange: true,
  });

  // Calculate current credits using new system
  // Use service_availability for SKIN_CLASSIFY if available, otherwise use total_available
  const skinClassifyService = subscriptionData?.service_availability?.SKIN_CLASSIFY;
  const currentCredits = skinClassifyService
    ? skinClassifyService.available
    : (subscriptionData?.total_available ?? (subscriptionData?.creds + subscriptionData?.extra_credits - subscriptionData?.counts) ?? 0);

  const handleBuyCredits = () => {
    onHide();
    // Navigate to subscription page where user can buy credits
    navigate('/account/subscribe');
  };

  // Temporarily disabled - subscription upgrade not available
  // const handleUpgradeSubscription = () => {
  //   onHide();
  //   // Navigate to subscription plans page
  //   navigate('/account?tab=subscription');
  // };

  const handleLearnMore = () => {
    // Could open a help page or external link about AI classification
    window.open('/help/ai-classification', '_blank');
  };

  return (
    <Modal show={show} onHide={onHide} size='md' centered>
      <Modal.Header closeButton className='border-0 pb-0'>
        <Modal.Title className='d-flex align-items-center'>
          {t('skinMoles.imageCarousel.creditsRequired.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='px-4 py-3'>
        <Alert variant='warning' className='mb-4'>
          <div className='d-flex align-items-start'>
            <div className='me-3' style={{ fontSize: '2rem' }}>
              💳
            </div>
            <div>
              <strong>{t('skinMoles.imageCarousel.creditsRequired.message')}</strong>
            </div>
          </div>
        </Alert>

        <Row className='mb-4'>
          <Col md={6}>
            <div className='text-center p-3 border rounded bg-light'>
              <div className='mb-2'>
                <strong>{t('skinMoles.imageCarousel.creditsRequired.currentCredits')}</strong>
              </div>
              <Badge 
                bg={currentCredits > 0 ? 'success' : 'danger'} 
                className='fs-5 px-3 py-2'
              >
                {currentCredits}
              </Badge>
            </div>
          </Col>
          <Col md={6}>
            <div className='text-center p-3 border rounded bg-light'>
              <div className='mb-2'>
                <strong>{t('skinMoles.imageCarousel.creditsRequired.creditsNeeded')}</strong>
              </div>
              <Badge bg='primary' className='fs-5 px-3 py-2'>
                {creditsNeeded}
              </Badge>
            </div>
          </Col>
        </Row>

        <div className='text-center mb-4'>
          <p className='text-muted'>
            {t('skinMoles.imageCarousel.creditsRequired.upgradeMessage')}
          </p>
        </div>

        <div className='d-grid gap-2'>
          {/* Primary action: Buy credits for one-time purchase */}
          <Button
            variant='primary'
            size='lg'
            onClick={handleBuyCredits}
            className='d-flex align-items-center justify-content-center gap-2'
          >
            <span>💰</span>
            {t('skinMoles.imageCarousel.creditsRequired.buyCredits')}
          </Button>

          {/* Secondary action: Upgrade subscription - temporarily disabled */}
          {/* 
          {!isSubscriptionActive && (
            <Button
              variant='outline-primary'
              size='lg'
              onClick={handleUpgradeSubscription}
              className='d-flex align-items-center justify-content-center gap-2'
            >
              <span>⭐</span>
              {t('skinMoles.imageCarousel.creditsRequired.upgradeSubscription')}
            </Button>
          )}
          */}

          {/* Learn more link */}
          <Button
            variant='link'
            onClick={handleLearnMore}
            className='text-muted'
            style={{ fontSize: '0.9rem' }}
          >
            {t('skinMoles.imageCarousel.creditsRequired.learnMore')}
          </Button>
        </div>
      </Modal.Body>

      <Modal.Footer className='border-0 pt-0'>
        <Button variant='secondary' onClick={onHide}>
          {t('skinMoles.imageCarousel.creditsRequired.cancel')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreditsRequiredModal;
